/**
 * DeleteServiceDialog Component (TASK-215 Phase 2)
 *
 * Confirmation dialog for deleting a service. Shows cascade warning
 * when the service has children (children_count > 0).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { servicesApi } from '@/services/api/services';
import type { Service } from '@/services/api/services';

interface DeleteServiceDialogProps {
  open: boolean;
  onClose: () => void;
  service: Service | null;
  onDeleted: () => void;
}

export function DeleteServiceDialog({
  open,
  onClose,
  service,
  onDeleted,
}: DeleteServiceDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: number) => servicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast.success('Услуга удалена');
      onDeleted();
    },
    onError: () => {
      toast.error('Ошибка при удалении услуги');
    },
  });

  const handleConfirm = () => {
    if (service) {
      deleteMutation.mutate(service.id);
    }
  };

  // Don't render anything when open=false or service is null
  if (!open || !service) return null;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <AlertDialogContent data-testid="delete-service-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить услугу</AlertDialogTitle>
          <AlertDialogDescription>
            Вы уверены, что хотите удалить{' '}
            <strong className="font-semibold text-gray-900">{service.name}</strong>?{' '}
            Это действие невозможно отменить.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {service.children_count > 0 && (
          <div
            data-testid="cascade-warning"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          >
            Внимание: у этой услуги есть дочерние подуслуги ({service.children_count}).
            Все подуслуги также будут удалены.
          </div>
        )}

        <AlertDialogFooter>
          <Button
            data-testid="cancel-delete-button"
            variant="outline"
            onClick={onClose}
            disabled={deleteMutation.isPending}
          >
            Отмена
          </Button>
          <Button
            data-testid="confirm-delete-button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && (
              <span data-testid="delete-loading">
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              </span>
            )}
            Удалить
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
